import { Contract, Utxo } from '@atomicfinance/bitcoin-dlc-provider';
import {
  Box,
  Button,
  CircularProgress,
  Flex,
  Heading,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Select,
  StackDivider,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/core';
import { decodeRawTransaction, getAddressNetwork } from '@liquality/bitcoin-utils';
import BigNumber from 'bignumber.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BtcImage from '../assets/btc.svg';
import TrophyImage from '../assets/trophy.png';
import config from '../config/config';
import { createClient, getClient } from '../services/chainClient';
import { RootState } from '../store/reducers';
import { sleep } from '../utils/Async';
import LineItem from './LineItem';

const EnterAddress = ({ btcAddress, setBtcAddress, onWithdraw, btcAddressError }) => (
  <Box textAlign="center">
    <Image src={BtcImage} mx="auto" height="75px" mb={1} />
    <Heading size="md" mb={2}>
      Please enter the BTC receiving address
    </Heading>
    <Input
      width="200px"
      placeholder="Enter your BTC address"
      value={btcAddress}
      onChange={(evt) => setBtcAddress(evt.target.value)}
    ></Input>
    {btcAddressError && <Text fontSize="sm" color="tomato">Invalid Bitcoin Address</Text>}
    <Flex mt="2rem">
      <Button
        ml="auto"
        backgroundColor="blue.600"
        color="white"
        onClick={onWithdraw}
      >
        Initiate Closing Bets
      </Button>
    </Flex>
  </Box>
);

const OverviewPage = ({
  setPage,
  totalTransferredBTC,
  feesBTC,
  sweepFeeBTC,
  totalPayoutWinningBTC,
  feeSpeed,
  setFeeSpeed
}) => {
  if (parseFloat(totalPayoutWinningBTC) === 0) {
    return (
      <Box textAlign="center">
        <CircularProgress
          // @ts-ignore
          isIndeterminate
          thickness="3px"
          size="80px"
          color="blue.600"
          mb={4}
        />
        <Heading mb={4} size="md">
          Loading
        </Heading>
      </Box>
    );
  }
  return (
    <Box>
      <Flex justifyContent="center" h="80px">
        <Image src={TrophyImage} maxW="full" maxH="full" />
      </Flex>

      <Heading size="sm" textAlign={'center'} mb={4} mt={4}>
        You proved that you put your money where your mouth is.
      </Heading>

      <VStack
        divider={<StackDivider borderColor="gray.400" />}
        spacing={4}
        align="stretch"
      >
        <VStack spacing={4} align="stretch">
          <LineItem label="Total Payout Winnings">
            {totalPayoutWinningBTC.toFixed(6)} BTC
          </LineItem>
          <LineItem label="Our 5% fees">{feesBTC.toFixed(6)} BTC</LineItem>
          <LineItem label="BTC Network Fees">
            <Select
              value={feeSpeed}
              ml="1px"
              onChange={(e) => setFeeSpeed(e.target.value)}
              fontWeight="bold"
              border="none"
              cursor="pointer"
            >
              <option value="fast">Fast {sweepFeeBTC['fast']?.toFixed(6)} BTC</option>
              <option value="medium">Medium {sweepFeeBTC['medium']?.toFixed(6)} BTC</option>
              <option value="slow">Slow {sweepFeeBTC['slow']?.toFixed(6)} BTC</option>
            </Select>
          </LineItem>
        </VStack>
        <VStack spacing={4} align="stretch">
          <LineItem label="Total Transferred">
            {totalTransferredBTC[feeSpeed]?.toFixed(6)} BTC
          </LineItem>
        </VStack>
      </VStack>

      <Flex mt="2rem">
        <Button
          ml="auto"
          backgroundColor="blue.600"
          color="white"
          onClick={() => setPage(1)}
        >
          Enter BTC Address
        </Button>
      </Flex>
    </Box>
  );
};

const WithdrawingPage = () => (
  <Box textAlign="center">
    <CircularProgress
      // @ts-ignore
      isIndeterminate
      thickness="3px"
      size="80px"
      color="blue.600"
      mb={4}
    />
    <Heading mb={4} size="md">
      Transferring your winnings...
    </Heading>
  </Box>
);

const CompletePage = ({ txHash }) => {
  const history = useHistory();
  return (
    <Box textAlign="center">
      <Heading mb={4} size="md">
        Transfer Complete!
      </Heading>

      <Heading as="h2" size="sm" mb={8} mt={8}>
        View Explorer:{' '}
        <Link
          textDecoration="underline"
          fontWeight="bold"
          href={`${config.bitcoin.explorer}/${txHash}`}
          target="_blank"
        >
          {txHash.substring(0, 15)}...
        </Link>
      </Heading>
      <Flex mt="2rem">
        <Button
          ml="auto"
          backgroundColor="blue.600"
          color="white"
          onClick={() => history.push('/dashboard')}
        >
          Go to Dashboard
        </Button>
      </Flex>
    </Box>
  );
};

const WithdrawAllWinningsModal = ({ isOpen }) => {
  const mnemonic = useSelector((state: RootState) => state.wallet.mnemonic);
  const bets = useSelector((state: RootState) => state.bets);

  const [page, setPage] = useState(0);
  const [btcAddress, setBtcAddress] = useState('');
  const [btcAddressError, setBtcAddressError] = useState(false);
  const [withdrawTxHash, setWithdrawTxHash] = useState('');

  const [txHexs, setTxHexs] = useState({});
  const [contracts, setContracts] = useState([] as Contract[]);
  const [allInputs, setAllInputs] = useState([] as any[]);

  const [totalTransferredBTC, setTotalTransferredBTC] = useState({});
  const [feesBTC, setFeesBTC] = useState(0);
  const [sweepFeeBTC, setSweepFeeBTC] = useState({});
  const [totalPayoutWinningBTC, setTotalPayoutWinningBTC] = useState(0);

  const [feePerBytes, setFeePerBytes] = useState({})
  const [feeSpeed, setFeeSpeed] = useState('fast')

  useEffect(() => {
    if (!mnemonic || !bets || !isOpen) return;
    (async () => {
      const bitcoin = createClient(mnemonic);

      const addresses = await bitcoin.getMethod('getAddresses')(0, 100);

      let _allInputs: any[] = [];
      let totalFee = new BigNumber(0);
      let payouts = new BigNumber(0);

      const _txHexs = {};

      const contracts: Contract[] = [];
      for (const betEntries of Object.entries(bets)) {
        const bet: any = betEntries[1];
        const { stage } = bet;

        const contract = bet.contract;
        const { id } = contract;

        if (stage === 'WITHDRAWABLE') {
          while (!bet.closeInfo) {
            await sleep(500);
          }
          const {
            inputs,
            fee,
            payout,
            cetTxHex,
            closingTxHex,
          } = await withdrawBuild(bet, id);

          _allInputs = _allInputs.concat(inputs);
          totalFee = new BigNumber(fee).dividedBy(1e8).plus(totalFee);
          payouts = payouts.plus(payout);
          _txHexs[contract.id] = { cetTxHex, closingTxHex };
          contracts.push(contract);
        }
      }

      const contractFinalAddresses: string[] = [];
      for (let i = 0; i < contracts.length; i++) {
        const contract = contracts[i];

        contractFinalAddresses.push(
          contract.isLocalParty
            ? contract.localPartyInputs.finalAddress
            : contract.remotePartyInputs.finalAddress
        );
      }
      const { address: payAddress } = await (
        await fetch(`${config.pay.url}/address`)
      ).json();

      const setInputs = _allInputs

      const network = await bitcoin.client.getMethod('getConnectedNetwork')();

      const speeds = [{ speed: 'fast', blocks: 1 }, { speed: 'medium', blocks: 6 }, { speed: 'slow', blocks: 12 }]

      const _totalTransferredBTC = totalTransferredBTC
      const _sweepFeeBTC = sweepFeeBTC
      const _feePerBytes = feePerBytes

      for (let i = 0; i < speeds.length; i++) {
        const { speed, blocks } = speeds[i]

        let feeEstimates
        try {
          feeEstimates = await (
            await fetch(`${config.bitcoin.api}/fee-estimates`)
          ).json();
        } catch(e) {
          feeEstimates = await (
            await fetch(`${config.bitcoin.altApi}/fee-estimates`)
          ).json();
        }

        const _feePerByte = Math.max(2, Math.ceil(feeEstimates[blocks.toString()] * (speed === 'fast' ? 1 : speed === 'medium' ? 0.8 : 0.6)))

        const outputs = [
          {
            to: payAddress,
            value: parseInt(new BigNumber(totalFee).times(1e8).toFixed()),
          },
        ];

        const {
          hex: _sweepTxHex,
        } = await bitcoin.client.finance.wallet.buildSweepTransactionWithSetOutputs(
          addresses[1].address,
          _feePerByte,
          outputs,
          setInputs
        );
        let sweepTx;
        let transferredIndex;
        try {
          sweepTx = decodeRawTransaction(_sweepTxHex, network);
          transferredIndex = sweepTx.vout[0].value > sweepTx.vout[1].value;
        } catch (e) {
          console.error(e);
        }

        const totalTransferred = sweepTx.vout[transferredIndex ? 0 : 1].value;
        const sweepFee = parseFloat(
          new BigNumber(payouts)
            .minus(totalFee)
            .minus(totalTransferred)
            .toFixed(8)
        )

        _totalTransferredBTC[speed] = totalTransferred
        _sweepFeeBTC[speed] = sweepFee
        _feePerBytes[speed] = _feePerByte
      }

      setTotalPayoutWinningBTC(parseFloat(payouts.toFixed(8)));
      setFeesBTC(parseFloat(totalFee.toFixed(8)));

      setTotalTransferredBTC(_totalTransferredBTC);
      setSweepFeeBTC(_sweepFeeBTC);
      setFeePerBytes(_feePerBytes)

      setTxHexs(_txHexs);
      setContracts(contracts);
      setAllInputs(_allInputs);
    })();
  }, [feesBTC, totalTransferredBTC, sweepFeeBTC, isOpen, bets, mnemonic]);

  const validateBtcAddress = async (address) => {
    try {
      const { name: networkName } = getAddressNetwork(address)

      if (networkName === config.bitcoin.network) {
        setBtcAddressError(false)
      } else {
        setBtcAddressError(true)
      }
    } catch(e) {
      setBtcAddressError(true)
    }

    setBtcAddress(address)
  }

  const onWithdrawAll = async () => {
    setPage(2);
    const bitcoin = getClient();

    await Promise.all(
      contracts.map(async (contract, index) => {
        let cetTxHash;
        try {
          cetTxHash = await bitcoin.getMethod('sendRawTransaction')(
            txHexs[contract.id].cetTxHex
          );
        } catch (e) {
          const cetTxid = decodeRawTransaction(txHexs[contract.id].cetTxHex)
            .txid;

          try {
            cetTxHash = (
              await bitcoin.getMethod('getTransactionByHash')(cetTxid)
            ).hash;

            console.log('Cet Tx already created');
          } catch (e) {
            throw Error(
              `Failed to sendRawTransaction cetTxHex and tx has not been previously broadcast. cetHex: ${
                txHexs[contract.id].cetTxHex
              }`
            );
          }
        }

        let closingTxHash;
        try {
          closingTxHash = await bitcoin.getMethod('sendRawTransaction')(
            txHexs[contract.id].closingTxHex
          );
        } catch (e) {
          const closingTxid = decodeRawTransaction(
            txHexs[contract.id].closingTxHex
          ).txid;

          try {
            closingTxHash = (
              await bitcoin.getMethod('getTransactionByHash')(closingTxid)
            ).hash;

            console.log('Closing Tx already created');
          } catch (e) {
            throw Error(
              `Failed to sendRawTransaction closingTxHex and tx has not been previously broadcast. closingHex: ${
                txHexs[contract.id].closingTxHex
              }`
            );
          }
        }

        const _txHexs = txHexs;
        _txHexs[contract.id].cetTxHash = cetTxHash;
        _txHexs[contract.id].closingTxHash = closingTxHash;

        setTxHexs(_txHexs);
      })
    );

    const { address: payAddress } = await (
      await fetch(`${config.pay.url}/address`)
    ).json();

    const outputs = [
      {
        to: payAddress,
        value: parseInt(new BigNumber(feesBTC).times(1e8).toFixed()),
      },
    ];

    const txHash = await bitcoin.client.finance.wallet.sendSweepTransactionWithSetOutputs(
      btcAddress,
      feePerBytes[feeSpeed],
      outputs,
      allInputs
    );

    setWithdrawTxHash(txHash.hash);
    setPage(3);
  };

  const withdrawBuild = async (bet, contractId) => {
    const bitcoin = getClient();
    const betClose = bet.closeInfo!;
    const { signature, outcomeIndex } = betClose;

    const contract = bet.contract;

    const addresses = await bitcoin.getMethod('getAddresses')(
      contract.startingIndex,
      2
    );

    const changeAddresses = await bitcoin.getMethod('getAddresses')(
      contract.startingIndex,
      2,
      true
    );

    const [
      cetTxHex,
      closingTxHex,
    ] = await bitcoin.client.finance.dlc.buildUnilateralClose(
      signature,
      outcomeIndex,
      contractId
    );

    const network = await bitcoin.client.getMethod('getConnectedNetwork')();
    const closingTx = decodeRawTransaction(closingTxHex, network);

    let utxos: Utxo[] = [];
    closingTx.vout.forEach((vout) => {
      const { n, scriptPubKey, value } = vout;
      utxos.push({
        txid: closingTx.txid,
        vout: n,
        amount: value,
        address: scriptPubKey.addresses[0],
      });
    });

    const addrList = addresses.concat(changeAddresses);

    let fixedInputs = utxos.map((utxo) => {
      const addr = addrList.find((a) => a.equals(utxo.address));
      console.log('addr', addr);
      console.log('utxo', utxo);
      return {
        ...utxo,
        value: new BigNumber(utxo.amount).times(1e8).toNumber(),
        derivationPath: addr.derivationPath,
      };
    });
    fixedInputs = fixedInputs.filter(
      (v, i, a) => a.findIndex((t) => t.txid === v.txid) === i
    );

    return {
      inputs: fixedInputs,
      fee: parseInt(
        new BigNumber(fixedInputs[0].value).times(config.fee).toFixed()
      ),
      payout: closingTx.vout[0].value,
      cetTxHex,
      closingTxHex,
    };
  };

  return (
    <Modal isOpen={isOpen} onClose={() => null} isCentered size={'xl'}>
      <ModalOverlay>
        <ModalContent>
          <ModalBody p={4}>
            <Tabs isLazy index={page}>
              <TabPanels>
                <TabPanel>
                  <OverviewPage
                    setPage={setPage}
                    totalTransferredBTC={totalTransferredBTC}
                    feesBTC={feesBTC}
                    sweepFeeBTC={sweepFeeBTC}
                    totalPayoutWinningBTC={totalPayoutWinningBTC}
                    feeSpeed={feeSpeed}
                    setFeeSpeed={setFeeSpeed}
                  />
                </TabPanel>
                <TabPanel>
                  <EnterAddress
                    setBtcAddress={validateBtcAddress}
                    btcAddress={btcAddress}
                    onWithdraw={onWithdrawAll}
                    btcAddressError={btcAddressError}
                  />
                </TabPanel>
                <TabPanel>
                  <WithdrawingPage />
                </TabPanel>
                <TabPanel>
                  <CompletePage txHash={withdrawTxHash} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default WithdrawAllWinningsModal;
